// src/pages/Configuraciones.js
import React, { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import AuthDetails from '../components/AuthDetails';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import {
    FaUserCog,
    FaQuestionCircle,
    FaUserEdit,
    FaDatabase,
    FaCogs,
    FaClock,
    FaCalendarCheck,
    FaShieldAlt,
    FaTimes,
    FaBookOpen,
    FaLifeRing,
    FaUpload,
    FaDollarSign,
    FaArrowRight,
    FaExchangeAlt,
    FaVolumeUp,
    FaFileInvoice,
} from 'react-icons/fa';
import { logEvent } from '../utils/analytics';
import { Capacitor } from '@capacitor/core';
import PrivacyPolicyLite from '../components/PrivacyPolicyLite';
import EditProfileModal from '../components/EditProfileModal';
import GestionDispositivosSesion from '../components/GestionDispositivosSesion';
import './Configuraciones.css';
import './ConfiguracionMobile.css';

const defaultTrafficRules = {
    speedVialidades: '20',
    speedEstacionamientos: '10',
    noiseDay: '55',
    noiseNight: '50',
    parkingRules: '- Respetar cajones asignados o de visitas\n- No bloquear accesos de garajes\n- No estacionarse en zonas de circulación o emergencia\n- Respetar espacios para personas con discapacidad',
    circulationRules: '- Ceder el paso a peatones siempre\n- Evitar circular en reversa salvo para estacionar\n- Respetar señalamientos internos (alto, ceda el paso)\n- No dar vuelta en U en áreas restringidas',
    scheduleRules: '- Evitar encender motores antes de las 6:00 o después de las 22:00\n- Restringir carga y descarga en horarios nocturnos\n- Mantener volumen moderado al ingresar o salir en la noche',
};

const Configuraciones = () => {
    const navigate = useNavigate();
    const { userData, currentUser } = useAuth();
    const { showNotification } = useNotification();
    const normalizedRole = String(userData?.rol || '').toLowerCase();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const canManageFraccionamiento = ['superadmin', 'admin', 'admin_fracc', 'platformadmin', 'manager'].includes(normalizedRole);
    const canConfigureTrafficRules = ['superadmin', 'platformadmin'].includes(normalizedRole);



    const [trafficRules, setTrafficRules] = useState(defaultTrafficRules);
    const [trafficLoading, setTrafficLoading] = useState(false);
    const [trafficSaving, setTrafficSaving] = useState(false);
    const [trafficModalOpen, setTrafficModalOpen] = useState(false);
    const [modulosHabilitados, setModulosHabilitados] = useState({});
    const [sonidosActivos, setSonidosActivos] = useState(userData?.preferencias?.sonidosHabilitados !== false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);


    useEffect(() => {
        setSonidosActivos(userData?.preferencias?.sonidosHabilitados !== false);
    }, [userData?.preferencias?.sonidosHabilitados]);

    useEffect(() => {
        if (!fraccionamientoId) return undefined;
        const ref = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsub = onSnapshot(ref, (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setModulosHabilitados(data.modulosHabilitados || {});
            }
        });
        return () => unsub();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!canConfigureTrafficRules || !fraccionamientoId) {
            return;
        }

        let active = true;
        const loadRules = async () => {
            try {
                setTrafficLoading(true);
                const ref = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(ref);
                if (!active) {
                    return;
                }
                if (snap.exists()) {
                    const stored = snap.data()?.reglasCirculacion;
                    if (stored && typeof stored === 'object') {
                        const merged = { ...defaultTrafficRules };
                        Object.keys(defaultTrafficRules).forEach((key) => {
                            if (Object.prototype.hasOwnProperty.call(stored, key) && stored[key] !== undefined && stored[key] !== null) {
                                merged[key] = String(stored[key]);
                            }
                        });
                        setTrafficRules(merged);
                    }
                }
            } catch (error) {
                console.error('Configuraciones: no se pudieron cargar las reglas de circulación', error);
            } finally {
                if (active) {
                    setTrafficLoading(false);
                }
            }
        };

        loadRules();

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canConfigureTrafficRules, fraccionamientoId]);




    const handleTrafficChange = (field) => (event) => {
        const value = event.target.value;
        setTrafficRules((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveTrafficRules = async (event) => {
        event.preventDefault();
        if (!canConfigureTrafficRules || !fraccionamientoId) {
            return;
        }

        try {
            setTrafficSaving(true);
            const ref = doc(db, 'fraccionamientos', fraccionamientoId);
            await setDoc(ref, { reglasCirculacion: trafficRules }, { merge: true });
            showNotification('Reglas de circulación actualizadas.', 'success');
        } catch (error) {
            console.error('Configuraciones: no se pudieron guardar las reglas de circulación', error);
            showNotification('No pudimos guardar las reglas. Intenta nuevamente.', 'error');
        } finally {
            setTrafficSaving(false);
        }
    };


    const accountItems = [
        {
            key: 'perfil',
            title: 'Editar Perfil',
            subtitle: 'Información personal',
            description: 'Actualiza tu nombre, correo electrónico y contraseña de acceso.',
            icon: FaUserEdit,
            color: '#3b82f6',
            bgColor: '#dbeafe',
            action: () => setIsProfileModalOpen(true),
        },
    ];

    const handleToggleSonidos = async () => {
        if (!currentUser) return;
        const newStatus = !sonidosActivos;

        // Actualización optimista del estado local
        setSonidosActivos(newStatus);

        try {
            const userRef = doc(db, 'usuarios', currentUser.uid);
            await setDoc(userRef, {
                preferencias: {
                    ...userData?.preferencias,
                    sonidosHabilitados: newStatus
                }
            }, { merge: true });
            console.log(`Sonidos ${newStatus ? 'activados' : 'desactivados'} en BD`);
            showNotification(`Sonidos ${newStatus ? 'activados' : 'desactivados'} correctamente.`, 'success');
        } catch (error) {
            console.error('Error al actualizar preferencia de sonidos:', error);
            // Revertir si falla
            setSonidosActivos(!newStatus);
            showNotification('No se pudo actualizar la preferencia de sonidos.', 'error');
        }
    };

    const helpItems = [
        {
            key: 'faq',
            title: 'Preguntas Frecuentes',
            subtitle: 'Guía de ayuda',
            description: 'Encuentra respuestas rápidas a las dudas más comunes sobre el uso de la plataforma.',
            icon: FaBookOpen,
            color: '#f59e0b',
            bgColor: '#fef3c7',
            path: '/configuraciones/faq',
        },
        {
            key: 'soporte',
            title: 'Contactar a Soporte',
            subtitle: 'Asistencia técnica',
            description: 'Reporta problemas, solicita ayuda o envía sugerencias al equipo de soporte.',
            icon: FaLifeRing,
            color: '#14b8a6',
            bgColor: '#ccfbf1',
            path: '/configuraciones/soporte',
        },
    ];

    const adminDataItems = [
        {
            key: 'importar',
            title: 'Importación Masiva',
            subtitle: 'Carga de datos',
            description: 'Sube archivos Excel para importar residentes, casas y otra información de forma masiva.',
            icon: FaUpload,
            color: '#8b5cf6',
            bgColor: '#ede9fe',
            path: '/configuraciones/importar',
        },
        {
            key: 'saldos',
            title: 'Gestión de Saldos',
            subtitle: 'Importar/Exportar saldos',
            description: 'Descarga template de saldos actuales, edítalo y vuelve a importar para actualizar balance de residentes.',
            icon: FaExchangeAlt,
            color: '#06b6d4',
            bgColor: '#cffafe',
            path: '/configuraciones/saldos',
            hidden: modulosHabilitados?.modulo_gestion_saldos === false
        },
        {
            key: 'importarHistorico',
            title: 'Importación de Histórico',
            subtitle: 'Carga de estados de cuenta',
            description: 'Sube archivos PDF o Excel con el historial completo de cargos, cuotas y abonos por casa o residente.',
            icon: FaFileInvoice,
            color: '#3b82f6',
            bgColor: '#dbeafe',
            path: '/configuraciones/importar-historico',
            hidden: modulosHabilitados?.modulo_importar_historico === false
        },
    ];

    const finalAdminDataItems = adminDataItems.filter(item => !item.hidden);


    const adminSettingsItems = [
        {
            key: 'mudanzas',
            title: 'Horarios de Mudanza',
            subtitle: 'Configuración de horarios',
            description: 'Define bloques de tiempo disponibles para mudanzas, construcción y remodelaciones.',
            icon: FaClock,
            color: '#3b82f6',
            bgColor: '#dbeafe',
            path: '/configuraciones/mudanzas',
        },
        {
            key: 'amenidades',
            title: 'Horarios de Amenidades',
            subtitle: 'Gestión de espacios',
            description: 'Ajusta los bloques disponibles y reglas de uso para cada amenidad activa.',
            icon: FaCalendarCheck,
            color: '#10b981',
            bgColor: '#d1fae5',
            path: '/configuraciones/amenidades-horarios',
        },
        {
            key: 'financiera',
            title: 'Configuración Financiera',
            subtitle: 'Cuotas y pagos',
            description: 'Configura cuotas mensuales, intereses moratorios, multas y otros ingresos.',
            icon: FaDollarSign,
            color: '#f59e0b',
            bgColor: '#fef3c7',
            path: '/configuraciones/financiera',
        },
        {
            key: 'circulacion',
            title: 'Reglas de Circulación',
            subtitle: 'Normativas internas',
            description: 'Consulta y actualiza límites de velocidad, niveles de ruido y horarios de restricción.',
            icon: FaShieldAlt,
            color: '#ef4444',
            bgColor: '#fee2e2',
            action: () => setTrafficModalOpen(true),
        },
        {
            key: 'recibos',
            title: 'Configuración de Recibos',
            subtitle: 'Personalización',
            description: 'Configura logo, datos fiscales, folio y etiquetas de conceptos para los recibos.',
            icon: FaDollarSign,
            color: '#6366f1',
            bgColor: '#e0e7ff',
            path: '/configuraciones/recibos',
        },
    ];

    // No agregues reglas de circulación a Mi Cuenta si es superadmin/platformadmin

    return (
        <div className="config-page">
            <AuthDetails />

            {/* Header */}
            <header className="config-header">
                <div className="config-header-content">
                    <h1 className="config-title">Configuraciones</h1>
                    <p className="config-subtitle">Gestiona tu cuenta y las opciones del sistema</p>
                </div>
            </header>
            <hr className="config-separator" />

            {/* Mi Cuenta */}
            <div className="config-section">
                <h2 className="config-section-title">
                    <FaUserCog /> Mi Cuenta
                </h2>
                <div className="config-cards-grid">
                    {accountItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                className="config-functional-card"
                                onClick={item.action}
                            >
                                <div className="config-card-header-section">
                                    <div className="config-card-icon-wrapper" style={{ backgroundColor: item.bgColor, color: item.color }}>
                                        <Icon />
                                    </div>
                                    <div className="config-card-title-section">
                                        <h3 className="config-card-title">{item.title}</h3>
                                        <p className="config-card-subtitle">{item.subtitle}</p>
                                    </div>
                                </div>
                                <p className="config-card-description">{item.description}</p>
                                <div className="config-card-footer-section">
                                    <span className="config-card-action">
                                        Acceder <FaArrowRight />
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Preferencias Rápidas */}
                <div className="config-preferences-row">
                    <div className="config-preference-item">
                        <div className="config-preference-info">
                            <FaVolumeUp style={{ color: '#3b82f6' }} />
                            <div>
                                <h4>Alertas sonoras</h4>
                                <p>Reproducir un sonido cuando llegue una nueva notificación.</p>
                            </div>
                        </div>
                        <label className="config-toggle">
                            <input
                                type="checkbox"
                                checked={sonidosActivos}
                                onChange={handleToggleSonidos}
                            />
                            <span className="config-toggle-slider"></span>
                        </label>
                    </div>
                </div>

                {/* Gestión de Dispositivos y Sesiones Activas (Plan B) */}
                <div style={{ marginTop: '1.5rem' }}>
                    <GestionDispositivosSesion />
                </div>
            </div>
            <hr className="config-separator" />

            {trafficModalOpen && (
                <div
                    className="config-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="traffic-modal-title"
                    onClick={() => setTrafficModalOpen(false)}
                >
                    <div className="config-modal" onClick={(event) => event.stopPropagation()}>
                        <button
                            type="button"
                            className="config-modal-close"
                            onClick={() => setTrafficModalOpen(false)}
                            aria-label="Cerrar reglas de circulación"
                        >
                            <FaTimes />
                        </button>
                        <div className="config-modal-card config-card-traffic">
                            <h4 id="traffic-modal-title">Reglas de circulación internas</h4>
                            <p className="config-card-subtitle">Define los lineamientos base para velocidad, ruido y comportamiento vehicular dentro del fraccionamiento.</p>
                            {trafficLoading ? (
                                <p className="config-status">Cargando reglas actuales…</p>
                            ) : (
                                <form className="config-form" onSubmit={handleSaveTrafficRules}>
                                    <div className="config-form-row">
                                        <div className="config-form-group">
                                            <label htmlFor="speedVialidades">Límite de velocidad en vialidades (km/h)</label>
                                            <input
                                                id="speedVialidades"
                                                type="number"
                                                min="5"
                                                max="40"
                                                value={trafficRules.speedVialidades}
                                                onChange={handleTrafficChange('speedVialidades')}
                                                disabled={trafficSaving}
                                            />
                                        </div>
                                        <div className="config-form-group">
                                            <label htmlFor="speedEstacionamientos">Límite en estacionamientos (km/h)</label>
                                            <input
                                                id="speedEstacionamientos"
                                                type="number"
                                                min="5"
                                                max="30"
                                                value={trafficRules.speedEstacionamientos}
                                                onChange={handleTrafficChange('speedEstacionamientos')}
                                                disabled={trafficSaving}
                                            />
                                        </div>
                                    </div>

                                    <div className="config-form-row">
                                        <div className="config-form-group">
                                            <label htmlFor="noiseDay">Nivel máximo de ruido diurno (dB)</label>
                                            <input
                                                id="noiseDay"
                                                type="number"
                                                min="30"
                                                max="80"
                                                value={trafficRules.noiseDay}
                                                onChange={handleTrafficChange('noiseDay')}
                                                disabled={trafficSaving}
                                            />
                                        </div>
                                        <div className="config-form-group">
                                            <label htmlFor="noiseNight">Nivel máximo de ruido nocturno (dB)</label>
                                            <input
                                                id="noiseNight"
                                                type="number"
                                                min="30"
                                                max="80"
                                                value={trafficRules.noiseNight}
                                                onChange={handleTrafficChange('noiseNight')}
                                                disabled={trafficSaving}
                                            />
                                        </div>
                                    </div>

                                    <div className="config-form-group">
                                        <label htmlFor="parkingRules">Reglas de estacionamiento</label>
                                        <textarea
                                            id="parkingRules"
                                            rows="6"
                                            value={trafficRules.parkingRules}
                                            onChange={handleTrafficChange('parkingRules')}
                                            disabled={trafficSaving}
                                            placeholder="- Respetar cajones asignados..."
                                        />
                                    </div>
                                    <div className="config-form-group">
                                        <label htmlFor="circulationRules">Reglas de circulación</label>
                                        <textarea
                                            id="circulationRules"
                                            rows="6"
                                            value={trafficRules.circulationRules}
                                            onChange={handleTrafficChange('circulationRules')}
                                            disabled={trafficSaving}
                                            placeholder="- Ceder el paso a peatones..."
                                        />
                                    </div>
                                    <div className="config-form-group">
                                        <label htmlFor="scheduleRules">Horarios y restricciones</label>
                                        <textarea
                                            id="scheduleRules"
                                            rows="6"
                                            value={trafficRules.scheduleRules}
                                            onChange={handleTrafficChange('scheduleRules')}
                                            disabled={trafficSaving}
                                            placeholder="- Evitar encender motores..."
                                        />
                                    </div>
                                    <div className="config-form-actions">
                                        <button type="submit" className="config-save-button" disabled={trafficSaving}>
                                            {trafficSaving ? 'Guardando…' : 'Guardar reglas'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Ayuda y Soporte */}
            <div className="config-section">
                <h2 className="config-section-title">
                    <FaQuestionCircle /> Ayuda y Soporte
                </h2>
                <div className="config-cards-grid">
                    {helpItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.key}
                                to={item.path}
                                className="config-functional-card"
                            >
                                <div className="config-card-header-section">
                                    <div className="config-card-icon-wrapper" style={{ backgroundColor: item.bgColor, color: item.color }}>
                                        <Icon />
                                    </div>
                                    <div className="config-card-title-section">
                                        <h3 className="config-card-title">{item.title}</h3>
                                        <p className="config-card-subtitle">{item.subtitle}</p>
                                    </div>
                                </div>
                                <p className="config-card-description">{item.description}</p>
                                <div className="config-card-footer-section">
                                    <span className="config-card-action">
                                        Acceder <FaArrowRight />
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>


            <hr className="config-separator" />


            {/* Panel de Administrador */}
            {canManageFraccionamiento && (
                <div className="config-section admin-section">
                    <h2 className="config-section-title">
                        <FaShieldAlt /> Panel de Administrador
                    </h2>

                    {/* Gestión de Datos */}
                    <div className="config-subsection">
                        <h3 className="config-subsection-title">
                            <FaDatabase /> Gestión de Datos
                        </h3>
                        <div className="config-cards-grid">
                            {finalAdminDataItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.key}
                                        to={item.path}
                                        className="config-functional-card"
                                    >
                                        <div className="config-card-header-section">
                                            <div className="config-card-icon-wrapper" style={{ backgroundColor: item.bgColor, color: item.color }}>
                                                <Icon />
                                            </div>
                                            <div className="config-card-title-section">
                                                <h3 className="config-card-title">{item.title}</h3>
                                                <p className="config-card-subtitle">{item.subtitle}</p>
                                            </div>
                                        </div>
                                        <p className="config-card-description">{item.description}</p>
                                        <div className="config-card-footer-section">
                                            <span className="config-card-action">
                                                Acceder <FaArrowRight />
                                            </span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                    <hr className="config-separator" />

                    {/* Ajustes del Fraccionamiento */}
                    <div className="config-subsection">
                        <h3 className="config-section-title">
                            <FaCogs /> Ajustes del Fraccionamiento
                        </h3>
                        <div className="config-cards-grid">
                            {adminSettingsItems.map((item) => {
                                const Icon = item.icon;
                                const CardComponent = item.path ? Link : 'button';
                                const cardProps = item.path
                                    ? { to: item.path, className: 'config-functional-card' }
                                    : {
                                        type: 'button',
                                        className: 'config-functional-card',
                                        onClick: item.action
                                    };
                                return (
                                    <CardComponent
                                        key={item.key}
                                        {...cardProps}
                                    >
                                        <div className="config-card-header-section">
                                            <div className="config-card-icon-wrapper" style={{ backgroundColor: item.bgColor, color: item.color }}>
                                                <Icon />
                                            </div>
                                            <div className="config-card-title-section">
                                                <h3 className="config-card-title">{item.title}</h3>
                                                <p className="config-card-subtitle">{item.subtitle}</p>
                                            </div>
                                        </div>
                                        <p className="config-card-description">{item.description}</p>
                                        <div className="config-card-footer-section">
                                            <span className="config-card-action">
                                                Acceder <FaArrowRight />
                                            </span>
                                        </div>
                                    </CardComponent>
                                );
                            })}
                        </div>
                    </div>
                    <hr className="config-separator" />

                    <div className="outlet-wrapper">
                        <Outlet />
                    </div>
                </div>
            )}


            {/* Aviso de Privacidad */}
            <div className="config-section">
                <PrivacyPolicyLite userId={currentUser?.uid} />
            </div>

            {/* Modales */}
            <EditProfileModal 
                isOpen={isProfileModalOpen} 
                onClose={() => setIsProfileModalOpen(false)} 
            />
        </div>
    );
};

export default Configuraciones;